import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mail,
  Phone,
  MessageSquare,
  Tag,
  ChevronDown,
  Plus,
  Calendar,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PilotCrmService } from '@/services/pilot-crm.service';
import { EmployerLead, PilotLeadStatus, PilotLeadPriority, LeadTaskType } from '@/models/pilot-crm.model';
import { PILOT_LEAD_STATUSES, PILOT_LEAD_PRIORITIES, PILOT_TASK_TYPES } from '@/data/pilot-crm-constants';
import { Button, FormInput, FormSelect, FormTextarea, Card } from '@/components/common';

export interface LeadQuickActionsProps {
  lead: EmployerLead;
  onEmailClick?: () => void;
  onCallClick?: () => void;
  compact?: boolean;
}

export const LeadQuickActions: React.FC<LeadQuickActionsProps> = ({
  lead,
  onEmailClick,
  onCallClick,
  compact = false,
}) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const updateMutation = useMutation({
    mutationFn: async (updates: { status?: PilotLeadStatus; priority?: PilotLeadPriority }) => {
      if (!profile?.id) throw new Error('Not authenticated');
      await PilotCrmService.updateLead(lead.id, updates, profile.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-leads'] });
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-lead', lead.id] });
      setShowStatusDropdown(false);
      setShowPriorityDropdown(false);
    },
  });

  const statusOptions = PILOT_LEAD_STATUSES.map((status) => ({
    value: status,
    label: status.replace(/_/g, ' '),
    isActive: status === lead.status,
  }));

  const priorityOptions = PILOT_LEAD_PRIORITIES.map((priority) => ({
    value: priority,
    label: priority.charAt(0).toUpperCase() + priority.slice(1),
    isActive: priority === lead.priority,
  }));

  if (compact) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <button
          className="p-1.5 rounded hover:bg-blue-50 text-gray-500 hover:text-blue-600 transition-colors"
          onClick={onEmailClick}
          title="Send email"
        >
          <Mail className="w-4 h-4" />
        </button>
        {lead.phone && (
          <button
            className="p-1.5 rounded hover:bg-green-50 text-gray-500 hover:text-green-600 transition-colors"
            onClick={onCallClick}
            title="Call"
          >
            <Phone className="w-4 h-4" />
          </button>
        )}
        <button
          className="p-1.5 rounded hover:bg-purple-50 text-gray-500 hover:text-purple-600 transition-colors"
          onClick={() => setShowNoteModal(true)}
          title="Add note"
        >
          <MessageSquare className="w-4 h-4" />
        </button>
        <button
          className="p-1.5 rounded hover:bg-orange-50 text-gray-500 hover:text-orange-600 transition-colors"
          onClick={() => setShowTaskModal(true)}
          title="Create task"
        >
          <Plus className="w-4 h-4" />
        </button>

        {showNoteModal && (
          <QuickNoteModal
            leadId={lead.id}
            onClose={() => setShowNoteModal(false)}
          />
        )}
        {showTaskModal && (
          <QuickTaskModal
            leadId={lead.id}
            onClose={() => setShowTaskModal(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <div className="relative">
        <button
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          onClick={() => setShowStatusDropdown(!showStatusDropdown)}
        >
          <Tag className="w-4 h-4" />
          <span className="capitalize">{lead.status.replace(/_/g, ' ')}</span>
          <ChevronDown className="w-4 h-4" />
        </button>
        {showStatusDropdown && (
          <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                  option.isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
                onClick={() => updateMutation.mutate({ status: option.value })}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative">
        <button
          className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border ${
            lead.priority === 'high'
              ? 'text-orange-700 bg-orange-50 border-orange-200'
              : lead.priority === 'low'
              ? 'text-gray-500 bg-gray-50 border-gray-200'
              : 'text-gray-700 bg-white border-gray-300'
          } hover:opacity-80`}
          onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
        >
          <span>{lead.priority}</span>
          <ChevronDown className="w-4 h-4" />
        </button>
        {showPriorityDropdown && (
          <div className="absolute z-50 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
            {priorityOptions.map((option) => (
              <button
                key={option.value}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                  option.isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
                onClick={() => updateMutation.mutate({ priority: option.value })}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
        onClick={onEmailClick}
      >
        <Mail className="w-4 h-4" />
        Email
      </button>

      {lead.phone && (
        <button
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100"
          onClick={onCallClick}
        >
          <Phone className="w-4 h-4" />
          Call
        </button>
      )}

      <button
        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100"
        onClick={() => setShowNoteModal(true)}
      >
        <MessageSquare className="w-4 h-4" />
        Note
      </button>

      <button
        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100"
        onClick={() => setShowTaskModal(true)}
      >
        <Plus className="w-4 h-4" />
        Task
      </button>

      {showNoteModal && (
        <QuickNoteModal
          leadId={lead.id}
          onClose={() => setShowNoteModal(false)}
        />
      )}
      {showTaskModal && (
        <QuickTaskModal
          leadId={lead.id}
          onClose={() => setShowTaskModal(false)}
        />
      )}
    </div>
  );
};

const QuickNoteModal: React.FC<{ leadId: string; onClose: () => void }> = ({
  leadId,
  onClose,
}) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id) throw new Error('Not authenticated');
      await PilotCrmService.createInteraction(
        {
          leadId,
          type: 'note',
          channel: 'in_app',
          summary: note,
        },
        profile.id
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-interactions', leadId] });
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-lead', leadId] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75"></div>
        <div className="relative max-w-md w-full z-10 bg-white rounded-lg shadow-xl p-6" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-bold text-gray-900">Quick Note</h3>
          </div>
          <FormTextarea
            label="Note"
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note..."
            autoFocus
          />
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => mutation.mutate()}
              disabled={!note.trim()}
              isLoading={mutation.isPending}
            >
              Save Note
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuickTaskModal: React.FC<{ leadId: string; onClose: () => void }> = ({
  leadId,
  onClose,
}) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [type, setType] = useState<LeadTaskType>('follow_up');
  const [dueAt, setDueAt] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id) throw new Error('Not authenticated');
      await PilotCrmService.createTask(
        {
          leadId,
          title,
          type,
          dueAt: new Date(dueAt),
        },
        profile.id
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-tasks', leadId] });
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-lead', leadId] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75"></div>
        <div className="relative max-w-md w-full z-10 bg-white rounded-lg shadow-xl p-6" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-bold text-gray-900">Quick Task</h3>
          </div>
          <div className="space-y-4">
            <FormInput
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Task title..."
              autoFocus
            />
            <FormSelect
              label="Type"
              options={PILOT_TASK_TYPES.map((t) => ({
                value: t,
                label: t.replace(/_/g, ' '),
              }))}
              value={type}
              onChange={(e) => setType(e.target.value as LeadTaskType)}
            />
            <FormInput
              label="Due Date"
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => mutation.mutate()}
              disabled={!title.trim() || !dueAt}
              isLoading={mutation.isPending}
            >
              Create Task
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
